/**
 * Resolves "?" dep_time / arr_time values in parsed flight rows
 * by searching sibling rows and then falling back to the database.
 */

import { supabase } from "@/integrations/supabase/client";
import type { ValidatedFlightPatternRow } from "./scheduleMarkdownParser";

/** Strip codeshare suffix: "PX019/CX6899" → "PX019" */
export function getBaseFlightNumber(fn: string): string {
  return fn.split("/")[0].trim();
}

function flightNumbersMatch(a: string, b: string): boolean {
  return getBaseFlightNumber(a) === getBaseFlightNumber(b);
}

async function queryTimeFromDb(
  baseFlight: string,
  airport: string,
  field: "dep_time" | "arr_time",
  airportField: "dep_airport" | "arr_airport"
): Promise<string | null> {
  const { data } = await supabase
    .from("flight_patterns")
    .select(field)
    .like("flight_number", `${baseFlight}%`)
    .eq(airportField, airport)
    .limit(1);

  if (data && data.length > 0) {
    const val = (data[0] as any)[field];
    if (val && val !== "?") {
      // DB returns TIME as "HH:MM:SS", normalise to "HH:MM"
      return String(val).substring(0, 5);
    }
  }
  return null;
}

export interface TimeResolutionResult {
  rows: ValidatedFlightPatternRow[];
  resolvedCount: number;
}

/**
 * Mutates rows in-place, resolving any "?" dep_time / arr_time.
 * Returns count of values that were successfully resolved.
 */
export async function resolveQuestionMarkTimes(
  rows: ValidatedFlightPatternRow[]
): Promise<TimeResolutionResult> {
  let resolvedCount = 0;

  for (const row of rows) {
    // ── dep_time ──
    if (row.dep_time === "?" || row.dep_time === "") {
      const resolved = await resolveTime(rows, row, "dep_time", "dep_airport");
      if (resolved) {
        row.dep_time = resolved;
        resolvedCount++;
        // Remove any prior unresolved warning for dep
        row.warnings = row.warnings.filter((w) => !w.includes("dep_time"));
      } else {
        if (!row.warnings.some((w) => w.includes("dep_time"))) {
          row.warnings.push("dep_time unresolved — row excluded from import");
        }
        row.valid = false;
      }
    }

    // ── arr_time ──
    if (row.arr_time === "?" || row.arr_time === "") {
      const resolved = await resolveTime(rows, row, "arr_time", "arr_airport");
      if (resolved) {
        row.arr_time = resolved;
        resolvedCount++;
        row.warnings = row.warnings.filter((w) => !w.includes("arr_time"));
      } else {
        if (!row.warnings.some((w) => w.includes("arr_time"))) {
          row.warnings.push("arr_time unresolved — row excluded from import");
        }
        row.valid = false;
      }
    }

    // Recalculate is_overnight after potential time changes
    if (row.dep_time !== "?" && row.arr_time !== "?" && row.dep_time !== "" && row.arr_time !== "") {
      row.is_overnight = row.arr_time < row.dep_time;
    }

    // Re-evaluate validity: valid only if no warnings remain
    row.valid = row.warnings.length === 0;
  }

  return { rows, resolvedCount };
}

async function resolveTime(
  allRows: ValidatedFlightPatternRow[],
  target: ValidatedFlightPatternRow,
  timeField: "dep_time" | "arr_time",
  airportField: "dep_airport" | "arr_airport"
): Promise<string | null> {
  // Step 1: Search sibling rows in the same parsed set
  const match = allRows.find(
    (r) =>
      r !== target &&
      r[timeField] !== "?" &&
      r[timeField] !== "" &&
      flightNumbersMatch(r.flight_number, target.flight_number) &&
      r[airportField] === target[airportField]
  );
  if (match) return match[timeField];

  // Step 2: Query database
  const baseFlight = getBaseFlightNumber(target.flight_number);
  const dbTime = await queryTimeFromDb(baseFlight, target[airportField], timeField, airportField);
  if (dbTime) return dbTime;

  // Step 3: Unresolved
  return null;
}
